import { useState, useEffect, useRef } from "react";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter } from "../lib/router-context";
import { authFetch } from "../lib/authFetch";
import {
  ArrowLeft, Loader2, AlertCircle, Briefcase, Download, CheckCircle,
  XCircle, MessageSquare, Send, Calendar, FileText, AlertTriangle, User, Phone, Mail,
  Star, Receipt, Ban,
} from "lucide-react";

interface PjSigned { name: string; size: number; url: string; }
interface DevisDetail {
  id: string; version: number;
  lignes: Array<{ description: string; quantite: number; prixUnitaire: number; total: number }>;
  montantHt: string | null; tauxTva: string | null; montantTtc: string | null;
  validiteJours: number | null; conditions: string | null;
  envoye: boolean | null; envoyeAt: string | null;
  accepte: boolean | null; accepteAt: string | null; accepteNote: string | null;
  estAvenant: boolean | null; avenantDe: string | null;
  createdAt: string;
}
interface Message { id: string; auteur: string; auteurType: string; contenu: string; createdAt: string; }
interface PrestationDetail {
  id: string; numero: string; statut: string; typePrestation: string; titre: string;
  description: string | null; niveauUrgence: string | null;
  siteNom: string | null; siteAdresse: string | null;
  clientFinalNom: string | null; clientFinalAdresse: string | null;
  contactTerrainNom: string | null; contactTerrainTel: string | null; contactTerrainEmail: string | null;
  modeleRobot: string | null; numeroSerie: string | null;
  descriptionPanne: string | null; piecesSuspectes: string[];
  dateIntervention: string | null; dateInterventionFin: string | null;
  piecesJointesSignees: PjSigned[];
  createdAt: string;
  devis: DevisDetail | null;
  messages: Message[];
  // Phase 2
  notation: number | null;
  notationCommentaire: string | null;
  notationAt: string | null;
  factureUrl: string | null;
  factureNumeroZoho: string | null;
  factureMontant: string | null;
  motifAnnulation: string | null;
}

const STATUT_LABELS: Record<string, string> = {
  demande_recue: "Demande reçue", devis_envoye: "Devis envoyé", devis_accepte: "Devis accepté",
  devis_refuse: "Devis refusé", intervention_planifiee: "Planifiée", intervention_realisee: "Réalisée",
  facture: "Facturée", clos: "Clôturée", annule: "Annulée",
};
const STATUT_COLORS: Record<string, string> = {
  demande_recue: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  devis_envoye: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  devis_accepte: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  devis_refuse: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  intervention_planifiee: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  intervention_realisee: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  facture: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  clos: "bg-secondary text-muted-foreground", annule: "bg-secondary text-muted-foreground",
};
const URGENCE_COLORS: Record<string, string> = {
  faible: "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400",
  normal: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400",
  urgent: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400",
  critique: "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400",
};
const URGENCE_LABELS: Record<string, string> = { faible: "Faible", normal: "Normal", urgent: "⚡ Urgent", critique: "🔴 Critique" };

function formatDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-muted-foreground w-32 shrink-0">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function StarRating({ value, onChange, readonly = false }: { value: number; onChange?: (v: number) => void; readonly?: boolean }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onChange?.(i)}
          onMouseEnter={() => !readonly && setHover(i)}
          onMouseLeave={() => !readonly && setHover(0)}
          className={`transition-colors ${readonly ? "cursor-default" : "cursor-pointer"}`}
          data-testid={`star-${i}`}
        >
          <Star
            className={`w-6 h-6 ${(hover || value) >= i ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/40"}`}
          />
        </button>
      ))}
    </div>
  );
}

export default function PrestationDetailPage({ id }: { id: string }) {
  const { portailUser, brand } = usePortailAuth();
  const { navigate, refreshNotifCount } = usePortailRouter();
  const primary = brand?.couleurPrimaire || "#2563eb";

  const [prestation, setPrestation] = useState<PrestationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const [reponseMode, setReponseMode] = useState<"accept" | "refuse" | null>(null);
  const [reponseNote, setReponseNote] = useState("");
  const [reponseMotif, setReponseMotif] = useState("");
  const [reponseLoading, setReponseLoading] = useState(false);
  const [reponseError, setReponseError] = useState<string | null>(null);
  const [reponseConfirmed, setReponseConfirmed] = useState(false);

  // Phase 2: notation
  const [notation, setNotation] = useState(0);
  const [notationCommentaire, setNotationCommentaire] = useState("");
  const [notationLoading, setNotationLoading] = useState(false);
  const [notationError, setNotationError] = useState<string | null>(null);
  const [notationSent, setNotationSent] = useState(false);

  // Phase 2: annulation
  const [showAnnuler, setShowAnnuler] = useState(false);
  const [motifAnnulation, setMotifAnnulation] = useState("");
  const [annulationLoading, setAnnulationLoading] = useState(false);
  const [annulationError, setAnnulationError] = useState<string | null>(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const res = await authFetch(`/api/portail/prestations/${id}`);
      if (!res.ok) { setError("Prestation introuvable."); return; }
      const data = await res.json();
      setPrestation(data);
      if (data.notation) setNotation(data.notation);
      if (data.notationCommentaire) setNotationCommentaire(data.notationCommentaire);
      if (data.notation) setNotationSent(true);
    } catch { setError("Erreur de chargement."); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    load();
    // Mark prestation notifications as read
    authFetch("/api/portail/notifications/prestations/lues", { method: "POST" }).then(() => refreshNotifCount()).catch(() => {});
  }, [id]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [prestation?.messages]);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true); setSendError(null);
    try {
      const res = await authFetch(`/api/portail/prestations/${id}/messages`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contenu: reply.trim() }),
      });
      if (!res.ok) { const err = await res.json(); setSendError(err.message || "Erreur."); return; }
      const msg = await res.json();
      setPrestation(prev => prev ? { ...prev, messages: [...prev.messages, msg] } : prev);
      setReply("");
    } catch { setSendError("Erreur réseau."); }
    finally { setSending(false); }
  }

  async function handleReponse(accepte: boolean) {
    if (!reponseConfirmed) { setReponseError("Veuillez cocher la confirmation légale."); return; }
    setReponseLoading(true); setReponseError(null);
    try {
      if (!accepte && !reponseMotif.trim()) { setReponseError("Un motif de refus est obligatoire."); setReponseLoading(false); return; }
      const endpoint = accepte ? "accepter" : "refuser";
      const res = await authFetch(`/api/portail/prestations/${id}/devis/${endpoint}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmationLegale: true, note: reponseNote, motif: reponseMotif }),
      });
      if (!res.ok) { const err = await res.json(); setReponseError(err.message || "Erreur."); return; }
      await load();
      setReponseMode(null); setReponseNote(""); setReponseMotif(""); setReponseConfirmed(false);
    } catch { setReponseError("Erreur réseau."); }
    finally { setReponseLoading(false); }
  }

  async function handleNoter() {
    if (!notation) { setNotationError("Veuillez sélectionner une note."); return; }
    setNotationLoading(true); setNotationError(null);
    try {
      const res = await authFetch(`/api/portail/prestations/${id}/noter`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notation, commentaire: notationCommentaire }),
      });
      if (!res.ok) { const err = await res.json(); setNotationError(err.message || "Erreur."); return; }
      setNotationSent(true);
      await load();
    } catch { setNotationError("Erreur réseau."); }
    finally { setNotationLoading(false); }
  }

  async function handleAnnuler() {
    if (!motifAnnulation.trim()) { setAnnulationError("Un motif est obligatoire."); return; }
    setAnnulationLoading(true); setAnnulationError(null);
    try {
      const res = await authFetch(`/api/portail/prestations/${id}/annuler`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ motif: motifAnnulation }),
      });
      if (!res.ok) { const err = await res.json(); setAnnulationError(err.message || "Erreur."); return; }
      setShowAnnuler(false);
      await load();
    } catch { setAnnulationError("Erreur réseau."); }
    finally { setAnnulationLoading(false); }
  }

  async function handleDownloadFacture() {
    try {
      const res = await authFetch(`/api/portail/prestations/${id}/facture`);
      if (!res.ok) return;
      const { url } = await res.json();
      window.open(url, "_blank");
    } catch {}
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );

  if (error || !prestation) return (
    <div className="p-6 space-y-4">
      <button onClick={() => navigate("/prestations")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      <div className="flex items-center gap-2 text-destructive p-4 bg-destructive/10 rounded-xl text-sm">
        <AlertCircle className="w-4 h-4" /> {error || "Prestation introuvable."}
      </div>
    </div>
  );

  const devis = prestation.devis;
  const canRepondre = devis && devis.envoye && devis.accepte === null;
  const canNoter = ["intervention_realisee", "facture", "clos"].includes(prestation.statut) && !prestation.notation;
  const canAnnuler = !["clos", "annule", "intervention_realisee", "facture"].includes(prestation.statut);

  return (
    <div className="max-w-4xl p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => navigate("/prestations")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="button-back">
            <ArrowLeft className="w-4 h-4" /> Retour aux demandes
          </button>
          {canAnnuler && (
            <button onClick={() => setShowAnnuler(true)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors border border-border"
              data-testid="btn-annuler">
              <Ban className="w-3.5 h-3.5" /> Annuler la demande
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono text-muted-foreground">{prestation.numero}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_COLORS[prestation.statut] || "bg-secondary text-muted-foreground"}`}>
            {STATUT_LABELS[prestation.statut] || prestation.statut}
          </span>
          {prestation.niveauUrgence && prestation.niveauUrgence !== "normal" && (
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${URGENCE_COLORS[prestation.niveauUrgence] || ""}`}>
              {URGENCE_LABELS[prestation.niveauUrgence]}
            </span>
          )}
          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{prestation.typePrestation}</span>
        </div>
        <h1 className="text-xl font-bold text-foreground mt-2">{prestation.titre}</h1>
        <p className="text-sm text-muted-foreground">Créée le {formatDate(prestation.createdAt)}</p>
      </div>

      {/* Annulation form */}
      {showAnnuler && (
        <div className="bg-destructive/5 border border-destructive/30 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-destructive flex items-center gap-2"><Ban className="w-4 h-4" /> Annuler la demande</p>
          <p className="text-xs text-muted-foreground">Cette action est définitive. L'équipe Jobotto sera notifiée.</p>
          <textarea
            className="w-full px-3 py-2 rounded-lg border border-destructive/40 bg-background text-sm focus:outline-none resize-none"
            rows={2} placeholder="Motif d'annulation (obligatoire)…"
            value={motifAnnulation} onChange={e => setMotifAnnulation(e.target.value)}
            data-testid="input-motif-annulation" />
          {annulationError && <p className="text-xs text-destructive">{annulationError}</p>}
          <div className="flex gap-2">
            <button onClick={() => { setShowAnnuler(false); setMotifAnnulation(""); setAnnulationError(null); }}
              className="flex-1 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:bg-secondary/60 transition">Retour</button>
            <button onClick={handleAnnuler} disabled={annulationLoading || !motifAnnulation.trim()}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-destructive text-white disabled:opacity-60 hover:opacity-90 transition"
              data-testid="btn-confirm-annuler">
              {annulationLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Confirmer l'annulation
            </button>
          </div>
        </div>
      )}

      {/* Annulation display */}
      {prestation.statut === "annule" && prestation.motifAnnulation && (
        <div className="bg-secondary border border-border/50 rounded-xl p-4 text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-1">Prestation annulée</p>
          <p>Motif : <em>{prestation.motifAnnulation}</em></p>
        </div>
      )}

      {/* ── Frise chronologique du workflow ── */}
      {(() => {
        const d = prestation.devis;
        type Step = { label: string; date: string | null; done: boolean; refused?: boolean };
        const WORKFLOW_STEPS: Array<Omit<Step, "done">> = [
          { label: "Demande reçue", date: prestation.createdAt, refused: false },
          { label: "Devis envoyé", date: d?.envoyeAt || null, refused: false },
          { label: d?.accepte === false ? "Devis refusé" : "Devis accepté", date: d?.accepteAt || null, refused: d?.accepte === false },
          { label: "Planifiée", date: prestation.dateIntervention || null, refused: false },
          { label: "Réalisée", date: null, refused: false },
          { label: "Clôturée", date: null, refused: false },
        ];
        const ORDER = ["demande_recue","devis_envoye","devis_accepte","devis_refuse","intervention_planifiee","intervention_realisee","facture","clos","annule"];
        const currentIdx = ORDER.indexOf(prestation.statut);
        const steps: Step[] = WORKFLOW_STEPS.map((s, i) => ({
          ...s, done: i === 0 || (i === 1 && currentIdx >= 1) || (i === 2 && currentIdx >= 2) || (i === 3 && currentIdx >= 4) || (i === 4 && currentIdx >= 5) || (i === 5 && currentIdx >= 7),
        }));
        return (
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Suivi de la demande</p>
            <div className="flex items-start gap-0">
              {steps.map((step, i) => (
                <div key={i} className="flex flex-1 flex-col items-center relative">
                  {i < steps.length - 1 && (
                    <div className={`absolute top-3 left-1/2 w-full h-0.5 ${step.done && steps[i+1]?.done ? "bg-green-500" : "bg-border/50"}`} style={{ zIndex: 0 }} />
                  )}
                  <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-xs border-2 shrink-0 transition-colors ${step.done ? step.refused ? "bg-red-500 border-red-500 text-white" : "bg-green-500 border-green-500 text-white" : "bg-background border-border text-muted-foreground"}`}>
                    {step.done ? (step.refused ? "✕" : "✓") : <span className="w-1.5 h-1.5 rounded-full bg-border block" />}
                  </div>
                  <div className="mt-2 text-center px-1">
                    <p className={`text-xs font-medium leading-tight ${step.done ? step.refused ? "text-red-600 dark:text-red-400" : "text-foreground" : "text-muted-foreground"}`}>{step.label}</p>
                    {step.date && <p className="text-xs text-muted-foreground mt-0.5">{formatDate(step.date)}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne gauche — infos + devis + facture */}
        <div className="lg:col-span-1 space-y-4">
          {/* Infos */}
          <div className="bg-card border border-border/50 rounded-xl p-4 space-y-2.5">
            <h3 className="text-sm font-semibold text-foreground mb-3">Détails</h3>
            {prestation.siteNom && <InfoRow label="Site" value={<span>{prestation.siteNom}{prestation.siteAdresse ? <span className="text-muted-foreground"> — {prestation.siteAdresse}</span> : ""}</span>} />}
            {prestation.clientFinalNom && <InfoRow label="Client final" value={prestation.clientFinalNom} />}
            {prestation.contactTerrainNom && (
              <div className="text-sm space-y-1 pt-1 border-t border-border/40 mt-1">
                <p className="text-xs text-muted-foreground font-medium">Contact terrain</p>
                <div className="flex items-center gap-1.5 text-foreground"><User className="w-3.5 h-3.5 text-muted-foreground" />{prestation.contactTerrainNom}</div>
                {prestation.contactTerrainTel && <div className="flex items-center gap-1.5 text-foreground"><Phone className="w-3.5 h-3.5 text-muted-foreground" />{prestation.contactTerrainTel}</div>}
                {prestation.contactTerrainEmail && <div className="flex items-center gap-1.5 text-foreground"><Mail className="w-3.5 h-3.5 text-muted-foreground" />{prestation.contactTerrainEmail}</div>}
              </div>
            )}
            {prestation.modeleRobot && <InfoRow label="Robot" value={<><Briefcase className="inline w-3.5 h-3.5 mr-1" />{prestation.modeleRobot}{prestation.numeroSerie ? ` (${prestation.numeroSerie})` : ""}</>} />}
            {prestation.dateIntervention && <InfoRow label="Date souhaitée" value={<><Calendar className="inline w-3.5 h-3.5 mr-1" />{formatDate(prestation.dateIntervention)}{prestation.dateInterventionFin ? ` → ${formatDate(prestation.dateInterventionFin)}` : ""}</>} />}
            {prestation.descriptionPanne && (
              <div className="pt-2 border-t border-border/40 space-y-1">
                <p className="text-xs text-muted-foreground">Panne</p>
                <p className="text-sm text-foreground whitespace-pre-wrap">{prestation.descriptionPanne}</p>
              </div>
            )}
            {prestation.piecesSuspectes?.length > 0 && (
              <div className="pt-1 space-y-1">
                <p className="text-xs text-muted-foreground">Pièces suspectes</p>
                <div className="flex flex-wrap gap-1.5">
                  {prestation.piecesSuspectes.map(p => <span key={p} className="text-xs px-2 py-0.5 bg-secondary rounded-full text-foreground">{p}</span>)}
                </div>
              </div>
            )}
            {prestation.description && (
              <div className="pt-2 border-t border-border/40">
                <p className="text-xs text-muted-foreground mb-1">Note</p>
                <p className="text-sm text-foreground whitespace-pre-wrap">{prestation.description}</p>
              </div>
            )}
          </div>

          {/* Pièces jointes */}
          {prestation.piecesJointesSignees?.length > 0 && (
            <div className="bg-card border border-border/50 rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Pièces jointes</h3>
              {prestation.piecesJointesSignees.map((pj, i) => (
                <a key={i} href={pj.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline">
                  <FileText className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{pj.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">({Math.round(pj.size / 1024)} Ko)</span>
                </a>
              ))}
            </div>
          )}

          {/* Facture */}
          {(prestation.statut === "facture" || prestation.statut === "clos") && (
            <div className="bg-card border border-border/50 rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Receipt className="w-4 h-4" /> Facture
              </h3>
              {prestation.factureNumeroZoho && (
                <p className="text-xs text-muted-foreground">Réf : <span className="text-foreground font-medium">{prestation.factureNumeroZoho}</span></p>
              )}
              {prestation.factureMontant && (
                <p className="text-xs text-muted-foreground">Montant : <span className="text-foreground font-medium">{Number(prestation.factureMontant).toFixed(2)} € HT</span></p>
              )}
              {prestation.factureUrl ? (
                <button onClick={handleDownloadFacture}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-white hover:opacity-80 transition w-full justify-center"
                  style={{ backgroundColor: primary }} data-testid="btn-download-facture">
                  <Download className="w-3.5 h-3.5" /> Télécharger la facture
                </button>
              ) : (
                <p className="text-xs text-muted-foreground italic">Facture en cours de préparation…</p>
              )}
            </div>
          )}

          {/* Devis */}
          {devis ? (
            <div className="bg-card border border-border/50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  Devis v{devis.version}
                  {devis.estAvenant && <span className="ml-1.5 text-xs text-amber-600 dark:text-amber-400 font-normal">(avenant)</span>}
                </h3>
                <button onClick={() => window.open(`/api/portail/prestations/${id}/devis/pdf`, "_blank")}
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg text-white hover:opacity-80 transition"
                  style={{ backgroundColor: primary }} data-testid="button-download-pdf">
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
              </div>

              <div className="text-sm space-y-1.5">
                {(devis.lignes || []).map((l, i) => (
                  <div key={i} className="flex justify-between text-muted-foreground">
                    <span className="flex-1 mr-2 truncate">{l.description} × {l.quantite}</span>
                    <span className="shrink-0">{Number(l.total).toFixed(2)} €</span>
                  </div>
                ))}
                <div className="border-t border-border/40 pt-1.5 flex justify-between font-semibold text-foreground">
                  <span>Total TTC</span><span>{Number(devis.montantTtc || 0).toFixed(2)} €</span>
                </div>
                {devis.validiteJours && <p className="text-xs text-muted-foreground">Validité : {devis.validiteJours} jours</p>}
              </div>

              {devis.accepte === true && <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm font-medium"><CheckCircle className="w-4 h-4" /> Devis accepté le {formatDate(devis.accepteAt)}</div>}
              {devis.accepte === false && <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm font-medium"><XCircle className="w-4 h-4" /> Devis refusé le {formatDate(devis.accepteAt)}</div>}

              {canRepondre && !reponseMode && (
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setReponseMode("accept")} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 transition" data-testid="btn-accept">
                    <CheckCircle className="w-3.5 h-3.5" /> Accepter
                  </button>
                  <button onClick={() => setReponseMode("refuse")} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 transition" data-testid="btn-refuse">
                    <XCircle className="w-3.5 h-3.5" /> Refuser
                  </button>
                </div>
              )}

              {reponseMode && (
                <div className="space-y-3 pt-2 border-t border-border/40">
                  <p className="text-sm font-medium text-foreground">{reponseMode === "accept" ? "✅ Confirmer l'acceptation" : "❌ Confirmer le refus"}</p>
                  {reponseMode === "refuse" && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-foreground">Motif du refus <span className="text-destructive">*</span></p>
                      <textarea className="w-full px-3 py-2 rounded-lg border border-destructive/50 bg-background text-sm focus:outline-none resize-none" rows={2} placeholder="Expliquez la raison du refus…" value={reponseMotif} onChange={e => setReponseMotif(e.target.value)} data-testid="input-motif" />
                    </div>
                  )}
                  <textarea className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none resize-none" rows={2} placeholder="Commentaire (optionnel)..." value={reponseNote} onChange={e => setReponseNote(e.target.value)} />
                  <label className="flex items-start gap-2.5 cursor-pointer text-xs text-muted-foreground">
                    <input type="checkbox" checked={reponseConfirmed} onChange={e => setReponseConfirmed(e.target.checked)} className="mt-0.5 shrink-0" data-testid="checkbox-legal" />
                    <span>Je confirme avoir pris connaissance du devis dans son intégralité et j'accepte les conditions générales de vente de Jobotto. En cochant cette case, j'appose ma <strong>signature électronique</strong> valable juridiquement.</span>
                  </label>
                  {reponseError && <p className="text-xs text-destructive">{reponseError}</p>}
                  <div className="flex gap-2">
                    <button onClick={() => { setReponseMode(null); setReponseNote(""); setReponseMotif(""); setReponseConfirmed(false); }} className="flex-1 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:bg-secondary/60 transition">Annuler</button>
                    <button onClick={() => handleReponse(reponseMode === "accept")} disabled={reponseLoading || !reponseConfirmed}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white disabled:opacity-60 hover:opacity-90 transition"
                      style={{ backgroundColor: reponseMode === "accept" ? "#16a34a" : "#dc2626" }}
                      data-testid="btn-confirm-reponse">
                      {reponseLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Confirmer
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-card border border-border/50 rounded-xl p-4 text-center text-sm text-muted-foreground">
              Un devis sera préparé par l'équipe Jobotto.
            </div>
          )}

          {/* Notation */}
          {(canNoter || prestation.notation) && (
            <div className="bg-card border border-border/50 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <Star className="w-4 h-4" /> {prestation.notation ? "Votre évaluation" : "Évaluer l'intervention"}
              </h3>
              {prestation.notation ? (
                <div className="space-y-2">
                  <StarRating value={prestation.notation} readonly />
                  {prestation.notationCommentaire && <p className="text-sm text-muted-foreground italic">"{prestation.notationCommentaire}"</p>}
                  <p className="text-xs text-muted-foreground">Noté le {formatDate(prestation.notationAt)}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">L'intervention est terminée. Comment s'est-elle passée ?</p>
                  <StarRating value={notation} onChange={setNotation} />
                  <textarea
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none resize-none"
                    rows={2} placeholder="Commentaire (optionnel)…"
                    value={notationCommentaire} onChange={e => setNotationCommentaire(e.target.value)}
                    data-testid="input-notation-commentaire" />
                  {notationError && <p className="text-xs text-destructive">{notationError}</p>}
                  <button onClick={handleNoter} disabled={notationLoading || !notation}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white disabled:opacity-60 hover:opacity-90 transition"
                    style={{ backgroundColor: primary }} data-testid="btn-noter">
                    {notationLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Envoyer mon évaluation
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Messagerie */}
        <div className="lg:col-span-2">
          <div className="bg-card border border-border/50 rounded-xl flex flex-col" style={{ minHeight: 480 }}>
            <div className="p-4 border-b border-border/60">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4" /> Discussion
                <span className="text-xs font-normal text-muted-foreground ml-1">({prestation.messages.length})</span>
              </h3>
            </div>
            <div className="flex-1 p-4 space-y-3 overflow-y-auto" style={{ maxHeight: 420 }}>
              {prestation.messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Aucun message pour le moment.</p>}
              {prestation.messages.map(msg => {
                const isMe = msg.auteurType === "portail";
                return (
                  <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm space-y-1 ${isMe ? "text-white" : "bg-secondary text-foreground"}`} style={isMe ? { backgroundColor: primary } : {}}>
                      <p className={`text-xs font-medium ${isMe ? "text-white/70" : "text-muted-foreground"}`}>{msg.auteur}</p>
                      <p className="whitespace-pre-wrap">{msg.contenu}</p>
                      <p className={`text-xs ${isMe ? "text-white/50" : "text-muted-foreground"}`}>{formatDate(msg.createdAt)}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            {prestation.statut !== "clos" && prestation.statut !== "annule" && (
              <form onSubmit={handleSend} className="p-4 border-t border-border/60">
                {sendError && <p className="text-xs text-destructive mb-2">{sendError}</p>}
                <div className="flex items-end gap-2">
                  <textarea className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none resize-none" rows={2} placeholder="Votre message..." value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e as any); } }} data-testid="input-reply" />
                  <button type="submit" disabled={sending || !reply.trim()} className="p-2.5 rounded-lg text-white disabled:opacity-50 hover:opacity-90 transition" style={{ backgroundColor: primary }} data-testid="btn-send">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
